import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot } from "vue"

import { onMounted, ref, watch, reactive, PropType, computed } from "vue";
import { useAsyncSpinner } from "@/services/Spinner";
import { useCart } from "@/store/useCart";
import { DataLayerApi, SearchApi } from "@/services/Api";
import { getSearchProducts } from "@/code/GoogleEventTracking";
import queryString from "query-string";
import { Pagination, ProductsSearchRequest, Sorting } from "@/models/Search";

type UrlSearchParams = {
  page: number | undefined;
  pageSize: number | undefined;
  sorting: Sorting | undefined;
};

const defaultPageSize = 20;

export default /*#__PURE__*/_defineComponent({
  __name: 'TressSearchPageWrapper',
  props: {
  totalHits: {
    type: Number,
    required: true,
  },
  term: {
    type: String,
    required: true,
  },
  showOnlyAgreementProducts: {
    type: Boolean,
    required: true,
  },
  pagination: {
    type: Object as PropType<Pagination>,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const cartStore = useCart();



onMounted(async () => {
  productListingsContainer.value = document.getElementById('product-listings-container');
});

const createUrlSearchParams = (url: string): UrlSearchParams => {
  const query = queryString.parse(url);
  return {
    page: query.page ? parseInt(query.page.toString(), 10) : undefined,
    pageSize: query.pageSize ? parseInt(query.pageSize.toString(), 10) : undefined,
    sorting: query.sorting as Sorting,
  };
};

const productListingsContainer = ref<HTMLElement | null>(null);
let urlSearchParams = reactive<UrlSearchParams>(createUrlSearchParams(location.search));
const currentPage = ref<number>(urlSearchParams.page ?? 1);
const currentPageSize = ref<number>(urlSearchParams.pageSize ?? defaultPageSize);
const totalPages = ref<number>();
const totalHits = ref<number>(props.totalHits);

const hideInitialProducts = ref<boolean>(false);
const products = ref<any>([]);
const favorited = ref<Array<string>>([]);
const isSortOpen = ref<boolean>(false);
const url = new URL(window.location.href);
const sorting = ref<Sorting>((url.searchParams.get('sorting') as Sorting) || Sorting.RelevanceDesc);
const showOnlyAgreementProducts = ref<boolean>(props.showOnlyAgreementProducts);
const paginationInfo = ref<Pagination>(props.pagination);
const pageSizeOptions = computed(() => paginationInfo.value.pageSizeOptions || [defaultPageSize]);
const toggleDisplayMode = () => {
  showOnlyAgreementProducts.value = !showOnlyAgreementProducts.value;
  reloadProducts();
};

const moveFocusToProductList = (): void => {
  const productListingsElement = document.getElementById('product-listings-focus-anchor') as HTMLElement | null;

  if (productListingsElement) {
    productListingsElement.focus({ preventScroll: true });
  }
};

const _setUrlParamAndPushState = (key: string, value: string) => {
  const url = new URL(window.location.href);
  url.searchParams.set(key, value);
  window.history.pushState(null, '', url);
};

const onSortChange = (ev) => {
  sorting.value = ev.target.value;
  _setUrlParamAndPushState('sorting', sorting.value.toString());
  reloadProducts();
};

const isFavorited = (id: string) => {
  return favorited.value.find((x) => x == id);
};

const onFavoriteClick = (id: string) => {
  const val = favorited.value.find((x) => x == id);
  if (val) {
    favorited.value = favorited.value.filter((x) => x != id);
  } else {
    favorited.value.push(id);
  }
};

const onCartClick = async (partNo: string, quantity: number): Promise<void> => {
  await useAsyncSpinner(async () => {
    await cartStore.addToCart(partNo, quantity, true);
    if (typeof window !== 'undefined' && window.BiTracking) {
      const row = cartStore.rows.find((r) => r.item.partNo === partNo);
      window.BiTracking.addToBasket(partNo, row?.quantity, '', cartStore.id, '');
    }
  });
};

const onMoveToTopClick = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const openSort = () => {
  isSortOpen.value = true;
};

const closeSort = () => {
  isSortOpen.value = false;
};

const onToggleSortClick = () => {
  if (isSortOpen.value) {
    closeSort();
  } else {
    openSort();
  }
};

const pageChange = async (newPage: number): Promise<void> => {
  currentPage.value = newPage;
  await changePage();
};

const pageSizeChange = async (newPageSize: number): Promise<void> => {
  currentPage.value = 1;
  currentPageSize.value = newPageSize;
  _setUrlParamAndPushState('pageSize', currentPageSize.value.toString());
  await changePage();
};

const changePage = async () => {
  if (productListingsContainer.value) {
    productListingsContainer.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  const request: ProductsSearchRequest = {
    term: props.term,
    pageNo: currentPage.value,
    totalPageSize: currentPageSize.value,
    sorting: sorting.value,
    requestFullResult: false,
    showOnlyAgreementProducts: showOnlyAgreementProducts.value,
  };

  _setUrlParamAndPushState('page', currentPage.value.toString());
  const res = await SearchApi.products(request);
  products.value = res.products;
  paginationInfo.value = res.pagination;
  currentPage.value = res.pagination.currentPage;
  totalPages.value = res.pagination.totalPages;
  totalHits.value = res.pagination.totalHits;

  moveFocusToProductList();
};

const reloadProducts = async () => {
  _setUrlParamAndPushState('page', currentPage.value.toString());

  const request: ProductsSearchRequest = {
    term: props.term,
    pageNo: currentPage.value,
    totalPageSize: currentPageSize.value,
    sorting: sorting.value,
    requestFullResult: false,
    showOnlyAgreementProducts: showOnlyAgreementProducts.value,
  };

  const res = await SearchApi.products(request);

  hideInitialProducts.value = true;
  products.value = res.products;

  moveFocusToProductList();

  paginationInfo.value = res.pagination;
  currentPage.value = res.pagination?.currentPage;
  totalPages.value = res.pagination?.totalPages;
};

const initialProducts = getSearchProducts();

watch(products, (currVal) => {
  const currValSkus = currVal.map((x) => x.sku);
  const combinedProducts = initialProducts.concat(currValSkus);
  DataLayerApi.createViewItemListEvent('search-page', combinedProducts);
});

return (_ctx: any,_cache: any) => {
  return _renderSlot(_ctx.$slots, "default", {
    hideInitialProducts: hideInitialProducts.value,
    onCartClick: onCartClick,
    isFavorited: isFavorited,
    onFavoriteClick: onFavoriteClick,
    onMoveToTopClick: onMoveToTopClick,
    pageChange: pageChange,
    pageSizeChange: pageSizeChange,
    products: products.value,
    isSortOpen: isSortOpen.value,
    onToggleSortClick: onToggleSortClick,
    sorting: sorting.value,
    onSortChange: onSortChange,
    showOnlyAgreementProducts: showOnlyAgreementProducts.value,
    onToggleDisplayMode: toggleDisplayMode,
    currentPage: currentPage.value,
    currentPageSize: currentPageSize.value,
    totalPages: totalPages.value,
    totalHits: totalHits.value,
    pageSizeOptions: pageSizeOptions.value
  })
}
}

})